// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useEffect, useState } from 'react';
import UAParser from 'ua-parser-js';

/**
 * iPad pros are a special case where they have a user agent string that makes them look like a desktop browser.
 */
const isIpadPro = (): boolean =>
  /Macintosh/i.test(navigator.userAgent) && !!navigator.maxTouchPoints && navigator.maxTouchPoints > 1;

const detectMobileSession = (): boolean => {
  const deviceType = new UAParser(window.navigator.userAgent).getDevice().type;
  return deviceType === 'mobile' || deviceType === 'tablet' || isIpadPro();
};

/**
 * Detect if the app is running in a mobile browser and updated if that changes (e.g. when switching to a mobile browser in a browser emulator)
 * @returns true if this detects the app is running in a mobile browser
 */
export const useIsMobile = (): boolean => {
  // Whenever the sample is changed from desktop -> mobile using the emulator, make sure we update the formFactor.
  const [isMobileSession, setIsMobileSession] = useState<boolean>(detectMobileSession());
  useEffect(() => {
    const updateIsMobile = (): void => {
      // The userAgent string is sometimes not updated synchronously when the `resize` event fires.
      setTimeout(() => {
        setIsMobileSession(detectMobileSession());
      });
    };
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  return isMobileSession;
};
